import { getDevice } from '@/api/config';
import DSButton from '@/components/design-system/Button';
import DSInput from '@/components/design-system/Input';
import useAuth from '@/contexts/AuthContext';
import { pushToDataLayer } from '@/helpers/Analytics';
import { ErrorHandler } from '@/helpers/ErrorHandler';
import { forgotPassQueryFn, googleLoginQueryFn, loginQueryFn } from '@/queries/auth';
import { useGoogleLogin } from '@react-oauth/google';
import { useMutation } from '@tanstack/react-query';
import { Formik } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import * as Yup from 'yup';

import EnvelopeIcon from 'public/assets/icons/envelope.svg';
import LockIcon from 'public/assets/icons/lock.svg';
import { AuthDto } from '@/api';
import Link from 'next/link';
import i18next from 'i18n';
import { getLandingPageTrackingFromLocalStorage } from '@/helpers/LandingPageTracker';
import DSIcon from '@/components/design-system/Icon';
import SocialLoginButton from '../SocialLoginButton';
import { useOnAccountCreated } from './useOnAccountCreated';
import { useOnAccountLogin } from './useOnAccountLogin';

const LoginSchema = Yup.object().shape({
    email: Yup.string().email(i18next.t('error-text.invalid-email')).required(i18next.t('error-text.required')),
    password: Yup.string()
        .required(i18next.t('error-text.required'))
});

const ForgotPasswordSchema = Yup.object().shape({
    email: Yup.string().email(i18next.t('error-text.invalid-email')).required(i18next.t('error-text.required'))
});

export type ILoginForm = {
    changeToSignup: () => void;
    isMobile?: boolean;
    /**
     * Keep the visitor here instead of sending them to onboarding when signing
     * in creates the account (Google). Set by surfaces embedded mid-flow.
     */
    skipOnboarding?: boolean;
};

const LoginForm = (props: ILoginForm) => {
    const [isLoginEmail, setIsLoginEmail] = useState<boolean>(false);
    const [isForgotPassword, setIsForgotPassword] = useState<boolean>(false);
    const { isMobile = false, changeToSignup, skipOnboarding } = props;
    const onAccountCreated = useOnAccountCreated(isMobile, { skipOnboarding });
    const { onAccountLogin, otpForm, showOtp } = useOnAccountLogin(isMobile);

    const { updateRefreshToken } = useAuth();

    const onLogin = (auth: AuthDto, created: boolean) => {
        const { user: resUser, refresh_token: refreshToken } = auth;

        if (refreshToken && refreshToken !== '') {
            updateRefreshToken(refreshToken);
        }

        pushToDataLayer({ event: created ? 'signup' : 'login' });

        if (created) {
            onAccountCreated(resUser);
        } else {
            onAccountLogin(resUser);
        }
    };

    const loginMutation = useMutation({
        mutationFn: loginQueryFn,
        onSuccess: (response) => {
            if (response && response.user) {
                onLogin(response, false);
            }
        },
        onError: async (error) => {
            const transformedError = await ErrorHandler.transformError(error);
            if (ErrorHandler.Auth.unauthorized(transformedError)) {
                toast.error(i18next.t('error-notification.invalid-login'));
            } else {
                toast.error(i18next.t('error-notification.unexpected-error'));
            }
        }
    });

    const googleLoginMutation = useMutation({
        mutationFn: googleLoginQueryFn,
        onSuccess: (response) => {
            if (response && response.user) {
                onLogin(response, response.created);
            }
        },
        onError: async (error) => {
            const transformedError = await ErrorHandler.transformError(error);
            if (ErrorHandler.Auth.unauthorized(transformedError)) {
                toast.error(i18next.t('error-notification.failed-google-login'));
            } else {
                toast.error(i18next.t('error-notification.unexpected-error'));
            }
        }
    });

    const googleLogin = useGoogleLogin({
        flow: 'auth-code',
        onSuccess: async (tokenResponse) => {
            googleLoginMutation.mutate({
                code: tokenResponse.code,
                device: await getDevice(),
                signed_up_from: getLandingPageTrackingFromLocalStorage()
            });
        }
    });

    const forgotPassword = useMutation({
        mutationFn: forgotPassQueryFn,
        onSuccess: () => {
            toast.success(i18next.t('success-notification.existing-email'));
            setIsForgotPassword(false);
        },
        onError: async (error) => {
            toast.error(i18next.t('error-notification.failed-send-verification-email'));
        }
    });

    const loginInputFields = useMemo(() => {
        return (
            <motion.div key="loginform" className="space-y-[12px]" exit={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <Formik
                    initialValues={{
                        email: '',
                        password: ''
                    }}
                    validationSchema={LoginSchema}
                    onSubmit={async (values) => {
                        try {
                            loginMutation.mutate({
                                body: {
                                    device: await getDevice(),
                                    email_address: values.email,
                                    password: values.password
                                }
                            });
                        } catch (error) {
                            toast.error(i18next.t('error-notification.recapcha'));
                        }
                    }}
                >
                    {({
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleSubmit
                    }) => (
                        <form onSubmit={handleSubmit}>
                            <div className="space-y-3 mt-4 relative">
                                <div>
                                    <DSInput
                                        autoFocus
                                        invalid={!!(errors.email && touched.email)}
                                        type="text"
                                        placeholder={i18next.t('input-text.email')}
                                        name="email"
                                        autoComplete="username"
                                        onChange={handleChange}
                                        value={values.email}
                                        icon={<EnvelopeIcon />}
                                    />
                                    {errors.email && touched.email ? (
                                        <div className="text-body-600 text-accent-red text-left">
                                            {errors.email}
                                        </div>
                                    ) : null}
                                </div>
                                <div>
                                    <DSInput
                                        invalid={!!(
                                            errors.password && touched.password
                                        )}
                                        name="password"
                                        type="password"
                                        autoComplete="current-password"
                                        placeholder={i18next.t('input-text.password')}
                                        onChange={handleChange}
                                        value={values.password}
                                        icon={<LockIcon />}
                                    />
                                    {errors.password && touched.password ? (
                                        <div className="text-body-600 text-accent-red text-left">
                                            {errors.password}
                                        </div>
                                    ) : null}
                                </div>
                                <DSButton
                                    icon={<DSIcon name="arrow-right" size={20} />}
                                    fullWidth
                                    loading={loginMutation.isPending || googleLoginMutation.isPending}
                                    disabled={loginMutation.isPending || googleLoginMutation.isPending}
                                    type="submit"
                                >
                                    {i18next.t('signin', { ns: 'header' })}
                                </DSButton>
                                <span className="text-body-400 block"><button onClick={() => setIsForgotPassword(true)} className="text-ink-400 underline">{i18next.t('button-text.forgot-password')}</button></span>
                            </div>
                        </form>
                    )}
                </Formik>
            </motion.div>
        );
    }, [googleLoginMutation.isPending, loginMutation]);

    const oAuthOptions = useMemo(() => {
        return (
            <motion.div key="loginoptions" className="space-y-[12px]" exit={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {/* <SocialLoginButton
                    network="apple"
                    disabled
                    onClick={() => {

                    }}
                >
                    APPLE
                </SocialLoginButton> */}
                <SocialLoginButton
                    network="google"
                    onClick={googleLogin}
                >
                    {i18next.t('general.google')}
                </SocialLoginButton>
                {isMobile ? (
                    <SocialLoginButton
                        network="email"
                        onClick={() => {
                            setIsLoginEmail(true);
                        }}
                    >
                        {i18next.t('general.login-with-email')}
                    </SocialLoginButton>
                ) : null}
            </motion.div>
        );
    }, [googleLogin, isMobile]);

    const forgotPasswordInputs = useMemo(() => {
        return (
            <motion.div key="forgotpwform" className="space-y-[12px]" exit={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <Formik
                    initialValues={{
                        email: ''
                    }}
                    validationSchema={ForgotPasswordSchema}
                    onSubmit={async (values) => {
                        forgotPassword.mutate({
                            email_address: values.email
                        });
                    }}
                >
                    {({
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleSubmit
                    }) => (
                        <form onSubmit={handleSubmit}>
                            <div className="space-y-3 relative">
                                <div>
                                    <DSInput
                                        autoFocus
                                        invalid={!!(errors.email && touched.email)}
                                        type="text"
                                        placeholder={i18next.t('input-text.email')}
                                        name="email"
                                        autoComplete="username"
                                        onChange={handleChange}
                                        value={values.email}
                                        icon={<EnvelopeIcon />}
                                    />
                                    {errors.email && touched.email ? (
                                        <div className="text-body-600 text-accent-red text-left">
                                            {errors.email}
                                        </div>
                                    ) : null}
                                </div>
                                <DSButton
                                    icon={<DSIcon name="arrow-right" size={20} />}
                                    fullWidth
                                    loading={forgotPassword.isPending}
                                    disabled={forgotPassword.isPending}
                                    type="submit"
                                >
                                    {i18next.t('button-text.reset-password')}
                                </DSButton>
                                <span className="text-body-400 block"><button onClick={() => setIsForgotPassword(false)}>{i18next.t('button-text.back-to-login')}</button></span>
                            </div>
                        </form>
                    )}
                </Formik>
            </motion.div>
        );
    }, [forgotPassword]);

    return (
        <div>
            <div className="text-center space-y-[24px] flex flex-col">
                <div>
                    <span className="text-h5 mb-[4px] uppercase text-ink-300">{i18next.t('general.log-in')}</span>
                    <h3 className="text-hero uppercase text-ink-400">{i18next.t('general.welcome-back')}</h3>
                </div>
                <div>
                    <AnimatePresence mode="wait">
                        {isMobile ? (
                            <>
                                {!isForgotPassword && !isLoginEmail && !showOtp ? oAuthOptions : null}
                                {isLoginEmail && !isForgotPassword && !showOtp ? loginInputFields : null}
                                {isLoginEmail && isForgotPassword && !showOtp ? forgotPasswordInputs : null}

                            </>
                        ) : (
                            <>
                                {!isForgotPassword && !showOtp ? oAuthOptions : null}
                                {!isForgotPassword && !showOtp ? <div className="text-body-400 my-4 text-ink-300">{i18next.t('general.or-login-with-email')}</div> : null}
                                {!isForgotPassword && !showOtp ? loginInputFields : null}
                                {isForgotPassword && !showOtp ? forgotPasswordInputs : null}
                            </>
                        )}
                        {showOtp &&
                            <motion.div key="otpForm" className="space-y-[12px]" exit={{ opacity: 0 }} animate={{ opacity: 1 }}>
                                {otpForm}
                            </motion.div>
                        }
                    </AnimatePresence>

                </div>
                {!showOtp && (
                    <>
                        <span className="text-body-400 block">{`${i18next.t('general.new-here')} `}<button onClick={() => changeToSignup()} className="text-ink-400 underline">{i18next.t('general.create-account')}</button></span>
                        <span className="text-body-600 block pt-[24px] text-ink-300">{`${i18next.t('disclaimer.authentication-form.pt1')} `}<Link href="/privacy-policy" target="_blank">{i18next.t('disclaimer.authentication-form.pt2')}</Link>{`. ${i18next.t('disclaimer.authentication-form.pt3')} `}<Link href="/terms-of-service" target="_blank">{i18next.t('disclaimer.authentication-form.pt4')}</Link>.</span>
                    </>
                )}
            </div>
        </div>
    );
};

export default LoginForm;
